import {Account, AssignAccountCommand, OcrAgreement, OnboardOcrCommand, RegisterOcrCommand, RemoveOcrCommand, UnassignAccountCommand, UpdateOcrCommand} from '@/bank/account/account.types'
import {get, post, put, postCatching, getCatching} from '@/bank/bankApi.service'
import {onMounted, onUnmounted, readonly, ref} from 'vue'
import emitter, {useSingletonEmitter} from '@/services/infrastructure/emitter'
import {LOGOUT, NO_ORG_SELECTED, ORG_SELECTED} from '@/types/events'
import {BANK_ACCOUNT_ASSIGNED, BANK_ACCOUNT_UNASSIGNED, BANK_ACCOUNT_UPDATED} from '@/bank/account/account.events'
import {notifyError} from '@/services/infrastructure/notification.service'

export const onboardOcr = async (cmd: OnboardOcrCommand): Promise<OcrAgreement> => {
    return await post('/accounts/:onboard-ocr', cmd)
}

export const registerOcr = (cmd: RegisterOcrCommand): Promise<OcrAgreement> => post('/accounts/:register-ocr', cmd)
export const removeOcr = (cmd: RemoveOcrCommand): Promise<void> => post('/accounts/:remove-ocr', cmd)
export const updateOcr = (cmd: UpdateOcrCommand): Promise<OcrAgreement> => put('/accounts/:update-ocr', cmd)

export const getOcrOnboardingStatus = async (accountUuid: string): Promise<OcrAgreement> => {
    return await get(`/accounts/${accountUuid}/:ocr-status`)
}


const accounts = ref<Account[]>([])
const isInitialized = ref(false)

export const useAccount = () => {
    const {onEvents, cleanUp} = useSingletonEmitter('banking:account-service')

    const fetchAccounts = async () => {
        const result = await getCatching<Account[]>('/accounts')
        if (result.isLeft()) {
            accounts.value = []
            notifyError({title: 'Kunne ikke hente kontoer', description: {descriptionMessage: result.error.detail, error: result.error}})
        } else {
            accounts.value = result.value
        }
    }

    const assignAccount = async (cmd: AssignAccountCommand) => {
        const result = await postCatching('/accounts/-/:assign', cmd, {tenantId: cmd.tenantId})
        if (result.isLeft()) {
            notifyError({title: 'Kunne ikke tildele konto', description: {descriptionMessage: result.error.detail, error: result.error}})
        } else {
            emitter.emit(BANK_ACCOUNT_ASSIGNED)
        }
    }

    const unassignAccount = async (cmd: UnassignAccountCommand) => {
        const result = await postCatching('/accounts/-/:unassign', cmd, {tenantId: cmd.tenantId})
        if (result.isLeft()) {
            notifyError({title: 'Kunne ikke fjerne tildeling av konto', description: {descriptionMessage: result.error.detail, error: result.error}})
        } else {
            emitter.emit(BANK_ACCOUNT_UNASSIGNED)
        }
    }

    if (!isInitialized.value) {
        isInitialized.value = true
        fetchAccounts()
    }

    onMounted(async () => {
        onEvents([ORG_SELECTED, BANK_ACCOUNT_UPDATED, BANK_ACCOUNT_ASSIGNED, BANK_ACCOUNT_UNASSIGNED], fetchAccounts)
        onEvents([NO_ORG_SELECTED, LOGOUT], () => {
            accounts.value = []
            isInitialized.value = false
        })
    })

    onUnmounted(() => {
        cleanUp()
    })

    return {
        accounts: readonly(accounts),
        assignAccount,
        unassignAccount,
        fetchAccounts
    }
}
